import React, { useCallback, useRef, useState } from 'react';
import { OrganIcon } from '../OrganSystemCircle/OrganIcons';
import './SystemIndex.css';

/**
 * Vertical organ system index for quick navigation through system-grouped lists.
 * Supports tap and drag/slide to jump between organ system sections.
 */
function SystemIndex({ systems, activeSystem, onSystemSelect }) {
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const getSystemFromPoint = useCallback((clientY) => {
        if (!containerRef.current) return null;
        const items = containerRef.current.querySelectorAll('.systemIndexItem');
        for (const item of items) {
            const rect = item.getBoundingClientRect();
            if (clientY >= rect.top && clientY <= rect.bottom) {
                return item.dataset.system;
            }
        }
        return null;
    }, []);

    const handleTouchStart = useCallback((e) => {
        setIsDragging(true);
        const system = getSystemFromPoint(e.touches[0].clientY);
        if (system) onSystemSelect(system);
    }, [getSystemFromPoint, onSystemSelect]);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        const system = getSystemFromPoint(e.touches[0].clientY);
        if (system) onSystemSelect(system);
    }, [getSystemFromPoint, onSystemSelect]);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    return (
        <div
            className={`systemIndex ${isDragging ? 'dragging' : ''}`}
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {systems.map(({ key, icon }) => (
                <div
                    key={key}
                    data-system={key}
                    className={`systemIndexItem ${activeSystem === key ? 'active' : ''}`}
                    onClick={() => onSystemSelect(key)}
                >
                    {key === 'all' ? (
                        <span className="systemIndexLabel">ABC</span>
                    ) : key === '_sys' ? (
                        <span className="systemIndexLabel">SYS</span>
                    ) : key === '_other' ? (
                        <span className="systemIndexLabel">O</span>
                    ) : key.startsWith('letter_') ? (
                        <span className="systemIndexLabel">{key.slice(7)}</span>
                    ) : (
                        <OrganIcon icon={icon} />
                    )}
                </div>
            ))}
        </div>
    );
}

export default SystemIndex;
